import { LoanLateFeeService } from '@/modules/global-settings/loan-origination/services/loan-policies/loan-late-fees.service';
import { make } from 'vuex-pathify';
import Logger from '@/lib/logger';
// Instantiate the service
const loanLateFeeService = new LoanLateFeeService();
// Define the state
export const state = {
  loanLateFeeSettings: [],
  loanLateFeeGeneralSettings: [],
  loanLateFeeFactors: [],
  loanLateFeeOptions: [],
  loanLateFeeBands: []
};
// Define the mutations
export const mutations = {
  ...make.mutations(state)
};
// Define the actions
export const actions = {
  /**
   * Fetch all loan late fee settings
   * @async
   * @param {object} context - The store context
   * @returns {Array} - The loan late fee settings
   * @private This will update the state with the fetched loan late fee settings
   */
  getLoanLateFeeSettings: async ({ commit }) => {
    try {
      const { data } = await loanLateFeeService.getLoanLateFeeSettings();
      commit('SET_LOAN_LATE_FEE_SETTINGS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan late fee settings', error);
      throw error;
    }
  },
  /**
   * Fetch the general loan late fee settings
   * @async
   * @param {object} context - The store context
   * @returns {Array} - The general loan late fee settings
   * @private This will update the state with the fetched general loan late fee settings
   */
  getLoanLateFeeGeneralSettings: async ({ commit }) => {
    try {
      const { data } = await loanLateFeeService.getLoanLateFeeGeneralSettings();
      commit('SET_LOAN_LATE_FEE_GENERAL_SETTINGS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan late fee general settings', error);
      throw error;
    }
  },
  /**
   * Fetch all loan late fee factors
   * @async
   * @param {object} context - The store context
   * @returns {Array} - The loan late fee factors
   * @private This will update the state with the fetched loan late fee factors
   */
  getLoanLateFeeFactors: async ({ commit }) => {
    try {
      const { data } = await loanLateFeeService.getLoanLateFeeFactors();
      commit('SET_LOAN_LATE_FEE_FACTORS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan late fee factors', error);
      throw error;
    }
  },
  /**
   * Fetch all loan late fee options
   * @async
   * @param {object} context - The store context
   * @returns {Object} data - The loan late fee options
   * @private This will update the state with the fetched loan late fee options
   */
  getLoanLateFeeOptions: async ({ commit }) => {
    try {
      const { data } = await loanLateFeeService.getLoanLateFeeOptions();
      commit('SET_LOAN_LATE_FEE_OPTIONS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan late fee factors', error);
      throw error;
    }
  },
  /**
   * Fetch all loan late fee bands
   * @async
   * @param {object} context - The store context
   * @returns {Array} - The loan late fee bands
   * @private This will update the state with the fetched loan late fee bands
   */
  getLoanLateFeeBands: async ({ commit }) => {
    try {
      const { data } = await loanLateFeeService.getLoanLateFeeBands();
      commit('SET_LOAN_LATE_FEE_BANDS', data);
      return data;
    } catch (error) {
      Logger.error('getting loan late fee bands', error);
      throw error;
    }
  },
  /**
   * Update the general loan late fee settings
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The payload object
   * @returns {void}
   * @private This will update the general loan late fee settings state
   */
  updateGeneralSettings: async ({ commit }, payload) => {
    try {
      const { data } = await loanLateFeeService.updateGeneralSettings(payload);
      commit('SET_LOAN_LATE_FEE_GENERAL_SETTINGS', data);
    } catch (error) {
      Logger.error('updating general loan late fee settings', error);
      throw error;
    }
  },
  /**
   * Update the loan late fee factors
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The payload object
   * @returns {void}
   * @private This will update the loan late fee factors state
   */
  updateFactors: async ({ commit }, payload) => {
    try {
      const { data } = await loanLateFeeService.updateFactors(payload);
      commit('SET_LOAN_LATE_FEE_FACTORS', data);
    } catch (error) {
      Logger.error('updating loan late fee factors', error);
      throw error;
    }
  },
  /**
   * Update the loan late fee options
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The payload object
   * @returns {void}
   * @private This will update the loan late fee options state
   */
  updateOptions: async ({ commit }, payload) => {
    try {
      const { data } = await loanLateFeeService.updateOptions(payload);
      commit('SET_LOAN_LATE_FEE_OPTIONS', data);
    } catch (error) {
      Logger.error('updating loan late fee options', error);
      throw error;
    }
  },
  /**
   * Update the loan late fee bands
   * @async
   * @param {object} context - The store context
   * @param {object} payload - The payload object
   * @returns {void}
   * @private This will update the loan late fee bands state
   */
  updateLateFeeBands: async ({ commit }, payload) => {
    try {
      const { data } = await loanLateFeeService.updateLateFeeBands(payload);
      commit('SET_LOAN_LATE_FEE_BANDS', data);
    } catch (error) {
      Logger.error('updating loan late fee bands', error);
      throw error;
    }
  }
};

// Define the getters
export const getters = make.getters(state);
